import React, { useContext, useState, useRef, useCallback } from "react";
import Popup from "reactjs-popup";
import { MachineContext } from "../fsm/useMachine";
import { SET_COLOR, ADD_BACKGROUND_MMO, DELETE } from "../fsm/types";
import { BackgroundIcon } from "../icons/BackgroundIcon";
import { XCircle } from "react-feather";
import Color, { TSColor } from "../../model/Color";
import ShapeFlowObject, { ShapeType } from "../../model/ShapeFlowObject";
import { GradientDef } from "../../ai/styles/themeDecorations";
import guidGenerator, { getCurrentDate } from "../utils/commonFunctions";
import GalleryAsset from "../../model/GalleryAsset";
import { addBackgroundImageHelper } from "./adding";
import { useSnackbar } from "notistack";

type TabType = "color" | "gradient" | "image";

const PRESET_COLORS = [
    "#FFFFFF", "#000000", "#333333", "#CCCCCC",
    "#1B2A4A", "#0D7377", "#F5F0E8", "#D6E4F0",
];

/* ---- shared inline style helpers ---- */

const label: React.CSSProperties = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--foreground)",
    marginBottom: 10,
    letterSpacing: 0.2,
};

const colorInput: React.CSSProperties = {
    width: 48,
    height: 36,
    border: "2px solid rgba(128,128,128,0.25)",
    borderRadius: 8,
    cursor: "pointer",
    background: "transparent",
    padding: 2,
};

const pill = (active: boolean): React.CSSProperties => ({
    padding: "7px 16px",
    borderRadius: 20,
    border: active ? "2px solid var(--foreground)" : "1px solid rgba(128,128,128,0.25)",
    background: active ? "var(--foreground)" : "transparent",
    color: active ? "var(--background)" : "var(--foreground)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
});

const primaryBtn: React.CSSProperties = {
    padding: "11px 28px",
    borderRadius: 10,
    border: "none",
    background: "var(--foreground)",
    color: "var(--background)",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0.3,
};

/* ---- component ---- */

export const BackgroundMenu = () => {
    const [current, send] = useContext(MachineContext);
    const [activeTab, setActiveTab] = useState<TabType>("color");
    const [bgMmoId, setBgMmoId] = useState<string | null>(null);
    const [applyToAll, setApplyToAll] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const [gradStartColor, setGradStartColor] = useState("#6366F1");
    const [gradEndColor, setGradEndColor] = useState("#EC4899");
    const [gradAngle, setGradAngle] = useState(180);
    const [gradType, setGradType] = useState<"linear" | "radial">("linear");
    const [radialPosition, setRadialPosition] = useState<string>("center");

    const fileInputRef = useRef<HTMLInputElement>(null);

    const { flow, index } = current.context;
    const currentScreen = flow.screens[index];
    const currentBgColor = currentScreen?.FlowScreenModelBackgroundColorKey;

    const hexFromColor = (c: Color | undefined): string => {
        if (!c) return "#000000";
        const r = Math.floor(c.r).toString(16).padStart(2, "0");
        const g = Math.floor(c.g).toString(16).padStart(2, "0");
        const b = Math.floor(c.b).toString(16).padStart(2, "0");
        return `#${r}${g}${b}`;
    };

    const removeBgMmo = useCallback(() => {
        if (bgMmoId) {
            send({ type: DELETE, id: bgMmoId, screenIndex: index });
            setBgMmoId(null);
        }
    }, [bgMmoId, send, index]);

    const handleTabChange = (tab: TabType) => {
        if (tab !== activeTab) removeBgMmo();
        setActiveTab(tab);
    };

    const setScreenColor = (hex: string) => {
        send({ type: SET_COLOR, color: TSColor.fromHex(hex) });
    };

    const applyGradient = () => {
        removeBgMmo();
        const viewSize = flow.viewSize;
        const gradientDef: GradientDef = {
            type: gradType,
            angle: gradAngle,
            position: gradType === 'radial' ? radialPosition : undefined,
            stops: [
                { color: gradStartColor, offset: 0 },
                { color: gradEndColor, offset: 100 },
            ],
        };
        const mkShape = () => {
            const s = new ShapeFlowObject(
                guidGenerator(), Color.iosToCssColor("00000000"), 0,
                GalleryAsset.default(), getCurrentDate(),
                { height: viewSize.height, width: viewSize.width, x: 0, y: 0 },
                null, 1, null, null, "shape", null, null, 0,
                { height: 0, width: 0 }, null, null,
                getCurrentDate(), false,
                Color.iosToCssColor("FFFFFFFF"), ShapeType.RECTANGLE, ""
            );
            s.gradientDef = gradientDef;
            return s;
        };
        if (applyToAll) {
            flow.screens.forEach((_: any, i: number) => {
                send({ type: ADD_BACKGROUND_MMO, mmo: mkShape(), screenIndex: i });
                send({ type: SET_COLOR, color: new Color(0, 0, 0, 0) });
            });
        } else {
            const shape = mkShape();
            send({ type: ADD_BACKGROUND_MMO, mmo: shape, screenIndex: index });
            setBgMmoId(shape.id);
            send({ type: SET_COLOR, color: new Color(0, 0, 0, 0) });
        }
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (e.target.files && e.target.files.length > 0) {
            removeBgMmo();
            enqueueSnackbar("Background image added...", { autoHideDuration: 2500, persist: false });
            addBackgroundImageHelper(e.target.files[0], flow.viewSize, send, index);
            send({ type: SET_COLOR, color: new Color(0, 0, 0, 0) });
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const radialPosMap: Record<string, string> = {
        'center': 'circle at center',
        'upper-left': 'circle at top left',
        'upper-right': 'circle at top right',
        'lower-left': 'circle at bottom left',
        'lower-right': 'circle at bottom right',
    };
    const gradientCSS =
        gradType === "linear"
            ? `linear-gradient(${gradAngle}deg, ${gradStartColor}, ${gradEndColor})`
            : `radial-gradient(${radialPosMap[radialPosition] || 'circle at center'}, ${gradStartColor}, ${gradEndColor})`;

    /* padding: the outer .shapes-content has 24px, we add generous inner padding */
    const modalPad: React.CSSProperties = { padding: "4px 12px" };

    return (
        <Popup
            trigger={
                <div className="div_styles button" title="Background">
                    <label><BackgroundIcon /></label>
                    <span>Background</span>
                </div>
            }
            closeOnDocumentClick closeOnEscape modal className="shapes"
        >
            {(close: () => void) => (
                <div style={modalPad}>

                    {/* ── Header ── */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 30 }}>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--foreground)" }}>Screen Background</h3>
                        <label onClick={(e) => { e.stopPropagation(); close(); }} style={{ cursor: "pointer", color: "var(--foreground)", opacity: 0.6 }}>
                            <XCircle className="feather" />
                        </label>
                    </div>

                    {/* ── Tabs ── */}
                    <div style={{ display: "flex", gap: 10, marginBottom: 32 }}>
                        {(["color", "gradient", "image"] as TabType[]).map((tab) => (
                            <button key={tab} style={pill(activeTab === tab)} onClick={() => handleTabChange(tab)}>
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </div>

                    {/* ── Color Tab ── */}
                    {activeTab === "color" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
                            <div>
                                <label style={label}>Color</label>
                                <input
                                    type="color"
                                    value={hexFromColor(currentBgColor)}
                                    onChange={(e) => setScreenColor(e.target.value)}
                                    style={colorInput}
                                />
                            </div>
                            <div>
                                <label style={label}>Presets</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                                    {PRESET_COLORS.map((hex) => (
                                        <div
                                            key={hex}
                                            onClick={() => setScreenColor(hex)}
                                            title={hex}
                                            style={{
                                                width: 42, height: 42, borderRadius: 10,
                                                border: "2px solid rgba(128,128,128,0.2)",
                                                backgroundColor: hex, cursor: "pointer",
                                                transition: "transform 0.15s ease, border-color 0.15s ease",
                                                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Gradient Tab ── */}
                    {activeTab === "gradient" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
                            {/* Preview */}
                            <div>
                                <label style={label}>Preview</label>
                                <div style={{
                                    width: "100%", height: 88, borderRadius: 12,
                                    border: "1px solid rgba(128,128,128,0.15)",
                                    background: gradientCSS,
                                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
                                }} />
                            </div>
                            {/* Stops */}
                            <div style={{ display: "flex", gap: 32 }}>
                                <div>
                                    <label style={label}>Start Color</label>
                                    <input type="color" value={gradStartColor} onChange={(e) => setGradStartColor(e.target.value)} style={colorInput} />
                                </div>
                                <div>
                                    <label style={label}>End Color</label>
                                    <input type="color" value={gradEndColor} onChange={(e) => setGradEndColor(e.target.value)} style={colorInput} />
                                </div>
                            </div>
                            {/* Type */}
                            <div>
                                <label style={label}>Type</label>
                                <div style={{ display: "flex", gap: 10 }}>
                                    <button style={pill(gradType === "linear")} onClick={() => setGradType("linear")}>Linear</button>
                                    <button style={pill(gradType === "radial")} onClick={() => setGradType("radial")}>Radial</button>
                                </div>
                            </div>
                            {/* Angle (linear) or Direction (radial) */}
                            {gradType === "linear" && (
                                <div>
                                    <label style={label}>Angle: {gradAngle}&deg;</label>
                                    <input type="range" min="0" max="360" value={gradAngle}
                                        onChange={(e) => setGradAngle(parseInt(e.target.value))}
                                        style={{ width: "100%", accentColor: "var(--foreground)" }}
                                    />
                                </div>
                            )}
                            {gradType === "radial" && (
                                <div>
                                    <label style={label}>Position</label>
                                    <div className="gradient-dir-picker">
                                        <button
                                            className={`gradient-dir-btn ${radialPosition === 'upper-left' ? 'active' : ''}`}
                                            onClick={() => setRadialPosition('upper-left')}
                                            title="Upper Left"
                                        >
                                            <svg viewBox="0 0 12 12" width="10" height="10">
                                                <line x1="10" y1="10" x2="3" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                <polyline points="3,7 3,3 7,3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                        <div className="gradient-dir-spacer" />
                                        <button
                                            className={`gradient-dir-btn ${radialPosition === 'upper-right' ? 'active' : ''}`}
                                            onClick={() => setRadialPosition('upper-right')}
                                            title="Upper Right"
                                        >
                                            <svg viewBox="0 0 12 12" width="10" height="10">
                                                <line x1="2" y1="10" x2="9" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                <polyline points="5,3 9,3 9,7" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                        <div className="gradient-dir-spacer" />
                                        <button
                                            className={`gradient-dir-btn gradient-dir-center ${radialPosition === 'center' ? 'active' : ''}`}
                                            onClick={() => setRadialPosition('center')}
                                            title="Center"
                                        >
                                            <svg viewBox="0 0 12 12" width="10" height="10">
                                                <circle cx="6" cy="6" r="3" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                                                <circle cx="6" cy="6" r="1" fill="currentColor"/>
                                            </svg>
                                        </button>
                                        <div className="gradient-dir-spacer" />
                                        <button
                                            className={`gradient-dir-btn ${radialPosition === 'lower-left' ? 'active' : ''}`}
                                            onClick={() => setRadialPosition('lower-left')}
                                            title="Lower Left"
                                        >
                                            <svg viewBox="0 0 12 12" width="10" height="10">
                                                <line x1="10" y1="2" x2="3" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                <polyline points="7,9 3,9 3,5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                        <div className="gradient-dir-spacer" />
                                        <button
                                            className={`gradient-dir-btn ${radialPosition === 'lower-right' ? 'active' : ''}`}
                                            onClick={() => setRadialPosition('lower-right')}
                                            title="Lower Right"
                                        >
                                            <svg viewBox="0 0 12 12" width="10" height="10">
                                                <line x1="2" y1="2" x2="9" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                <polyline points="9,5 9,9 5,9" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div>
                                <button style={primaryBtn} onClick={applyGradient}>Apply Gradient</button>
                            </div>
                        </div>
                    )}

                    {/* ── Image Tab ── */}
                    {activeTab === "image" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
                            <p style={{ fontSize: 14, color: "var(--foreground)", margin: 0, lineHeight: 1.7, opacity: 0.8 }}>
                                Upload an image to use as the screen background. It will be sized to fill the full canvas.
                            </p>
                            <div>
                                <input ref={fileInputRef} type="file" accept=".png,.gif,.jpg,.jpeg" onChange={handleImageUpload} style={{ display: "none" }} />
                                <button style={primaryBtn} onClick={() => fileInputRef.current?.click()}>Choose Image</button>
                            </div>
                        </div>
                    )}

                    {/* ── Footer ── */}
                    <div style={{ marginTop: 36, paddingTop: 22, borderTop: "1px solid rgba(128,128,128,0.15)" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--foreground)", cursor: "pointer", opacity: 0.85 }}>
                            <input type="checkbox" checked={applyToAll} onChange={(e) => setApplyToAll(e.target.checked)}
                                style={{ width: 16, height: 16, accentColor: "var(--foreground)" }}
                            />
                            Apply to all screens
                        </label>
                    </div>
                </div>
            )}
        </Popup>
    );
};

export default BackgroundMenu;
